import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  InputGroup,
  InputRightElement,
  IconButton,
  FormErrorMessage,
  Box,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuthStore } from '../store/authStore';

const RegisterPage = () => {
  const navigate = useNavigate();
  // Get checkEmailExists and signUp from the store
  const { checkEmailExists, signUp, loading } = useAuthStore(); 
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  // State to manage the form step
  const [emailCheckComplete, setEmailCheckComplete] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false); // Loading state for email check

  const validateEmail = (emailToValidate: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = regex.test(emailToValidate);
    setIsEmailInvalid(!isValid);
    return isValid;
  };

  const validatePassword = () => {
    let isValid = true;
    if (password.length < 8) {
      setIsPasswordInvalid(true);
      setPasswordErrorMessage('Password must be at least 8 characters');
      isValid = false;
    } else if (password !== confirmPassword) {
      setIsPasswordInvalid(true);
      setPasswordErrorMessage('Passwords do not match');
      isValid = false;
    } else {
      setIsPasswordInvalid(false);
      setPasswordErrorMessage('');
    }
    return isValid;
  };

  // Handler for the initial email check
  const handleCheckEmail = async () => {
    setError('');
    setIsEmailInvalid(false);
    if (!validateEmail(email)) {
      console.log('Email validation failed.');
      return;
    }

    console.log('Starting email check...');
    setIsCheckingEmail(true);
    try {
      const exists = await checkEmailExists(email);
      console.log('Email exists check result:', exists); // Log the result

      if (exists === true) { // Explicit check for true
        console.log('Email exists, showing toast and setting invalid.');
        toast({
          title: 'Email Already Registered',
          description: `The email ${email} is already associated with an account. Please sign in instead.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setIsEmailInvalid(true); // Mark email as invalid visually
        setEmailCheckComplete(false); // Ensure password fields stay hidden
      } else {
        // Email is unique, proceed to password step
        console.log('Email does not exist, setting check complete.');
        setEmailCheckComplete(true);
        setIsEmailInvalid(false); // Ensure email is marked as valid
      }
    } catch (err: any) {
      console.error('Email check error:', err);
      setError(err.message || 'Failed to check email. Please try again.');
      toast({
        title: 'Error Checking Email',
        description: err.message || 'An unexpected error occurred.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      setEmailCheckComplete(false); // Reset on error
    } finally {
      console.log('Finished email check.');
      setIsCheckingEmail(false);
    }
  };

  // Handler for the final form submission (after email check)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Clear previous errors
    setIsPasswordInvalid(false);
    setPasswordErrorMessage('');

    // Should not happen if button is disabled, but double-check
    if (!emailCheckComplete) return; 

    // Validate password
    if (!validatePassword()) {
      return;
    }

    try {
      // Attempt sign up (email uniqueness already checked)
      await signUp(email, password);

      console.log('✅ Signup successful on RegisterPage, navigating to verification.');
      localStorage.setItem('pendingVerificationEmail', email); // Store email for verification page
      navigate('/verify-email');

    } catch (err: any) {
      console.error('Registration error caught in component:', err);

      // Handle errors from the actual signUp call (e.g., weak password, network error)
      if (err.message.includes('stronger password')) {
        setError('Please use a stronger password.');
        setIsPasswordInvalid(true);
        setPasswordErrorMessage('Password is too weak. Try adding numbers or symbols.');
      } else if (err.message.includes('sending confirmation email') || err.message.includes('Error sending email')) {
        toast({
          title: 'Email Service Issue',
          description: 'We had trouble sending the confirmation email. Please try again later or contact support.',
          status: 'warning',
          duration: 7000,
          isClosable: true,
          position: 'top',
        });
        setError('Could not send confirmation email. Please check back later.');
      } else {
        // Handle any other unexpected errors from signUp
        const displayError = err.message || 'Failed to sign up due to an unexpected error.';
        setError(displayError);
        toast({
          title: 'Registration Failed',
          description: displayError,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start tracking your billable hours"
    >
      {/* Use different submit handlers based on the step */}
      {/* IMPORTANT: Ensure the form onSubmit correctly calls handleCheckEmail when appropriate */}
      <form onSubmit={(e) => {
          e.preventDefault(); 
          if (emailCheckComplete) {
            handleSubmit(e); // Call final submit if check is done
          } else {
            handleCheckEmail(); // Call email check otherwise
          }
        }}>
        <Stack spacing={4}>
          {/* General Error Alert */}
          {error && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Registration Failed</AlertTitle>
                <AlertDescription fontSize="sm">{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Email Input (Always Visible) */}
          <FormControl id="email" isRequired isInvalid={isEmailInvalid}>
            <FormLabel>Email address</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setIsEmailInvalid(false);
                setError('');
                // If user changes email, reset the password step
                if (emailCheckComplete) {
                  console.log('Email changed, resetting password step.'); // Log reset
                  setEmailCheckComplete(false);
                  setPassword('');
                  setConfirmPassword('');
                  setIsPasswordInvalid(false);
                  setPasswordErrorMessage('');
                }
              }}
              // This makes the input read-only when emailCheckComplete is true
              isReadOnly={emailCheckComplete}
              // Add custom styling for the read-only state
              _readOnly={{
                bg: 'gray.200', // Darker background when read-only
                opacity: 0.8, // Slightly reduce opacity for visual distinction
                cursor: 'not-allowed', // Indicate non-interactive state
              }}
            />
            {isEmailInvalid && (
              <FormErrorMessage>Please enter a valid email address or the email is already registered.</FormErrorMessage>
            )}
          </FormControl>

          {/* Password Fields (Conditionally Rendered) */}
          {emailCheckComplete && (
            <Stack spacing={4} mt={4}> {/* Add margin top for spacing */}
              <FormControl id="password" isRequired isInvalid={isPasswordInvalid}>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    name="password" // Add name attribute
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setIsPasswordInvalid(false);
                    }}
                  />
                  <InputRightElement width="4.5rem">
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl id="confirmPassword" isRequired isInvalid={isPasswordInvalid}>
                <FormLabel>Confirm Password</FormLabel>
                <Input
                  name="confirmPassword" // Ensure name attribute is present
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setIsPasswordInvalid(false);
                  }}
                />
                {isPasswordInvalid && (
                  <FormErrorMessage>{passwordErrorMessage}</FormErrorMessage>
                )}
              </FormControl>
            </Stack>
          )}

          <Stack spacing={6} pt={2}>
            {/* Button changes text and action based on step */}
            <Button
              type="submit"
              colorScheme="blue"
              // Use combined loading state
              isLoading={isCheckingEmail || loading} 
              loadingText={isCheckingEmail ? 'Checking Email...' : 'Creating account...'}
              // Disable button if email is invalid *before* check is complete
              isDisabled={!emailCheckComplete && isEmailInvalid}
            >
              {emailCheckComplete ? 'Create account' : 'Continue'}
            </Button>
          </Stack>

          <Stack pt={6}>
            <Link as={RouterLink} to="/login" color="blue.500" textAlign="center">
              Already have an account? Sign in
            </Link>
          </Stack>
        </Stack>
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;